/**
 * Alpine component powering the price calculator.
 *
 * On load it either creates a new (empty) estimate via POST /price-calculator/estimates,
 * or - if a `?uid=` is already present in the URL - loads that existing one via GET
 * /price-calculator/estimates/{uid}. Every edit is auto-saved (debounced) via PUT
 * /price-calculator/estimates/{uid}, so the current URL is always a valid share link.
 *
 * Visitors either search for their MAT/school group (GET /price-calculator/trusts),
 * which pre-fills the schools list from our own trust/school directory (imported
 * weekly from the DfE GIAS export - see ImportSchoolData), or build a custom
 * estimate by adding establishments manually.
 */

// Phases the Insight product currently supports pricing for - mirrors the old
// WordPress plugin's supportedPhases list.
const SUPPORTED_PHASES = [
    "All-through",
    "Middle deemed primary",
    "Nursery",
    "Primary",
];

export default function priceCalculator() {
    return {
        // ── Data ──
        loading: true,
        error: "",
        uid: null,
        saving: false,
        copied: false,
        editingPrices: false,

        // ── Search ──
        searchQuery: "",
        searching: false,
        searchError: "",
        searchResults: [],

        name: "",
        schools: [],
        excludedSchools: [],
        pricePerPupil: 4.5,
        maxPrice: 2250,
        upperBand: 499,
        discounts: [0, 10, 15],

        _nextCustomId: 1,
        _saveTimeout: null,

        async init() {
            const params = new URLSearchParams(window.location.search);
            this.uid = params.get("uid");

            if (this.uid) {
                await this.load();
            } else {
                await this.create();
            }

            this.loading = false;

            this.$watch(
                () =>
                    JSON.stringify({
                        name: this.name,
                        schools: this.schools,
                        pricePerPupil: this.pricePerPupil,
                        maxPrice: this.maxPrice,
                        upperBand: this.upperBand,
                        discounts: this.discounts,
                    }),
                () => this.scheduleSave()
            );
        },

        async create() {
            try {
                const response = await fetch("/price-calculator/estimates", {
                    method: "POST",
                    headers: { Accept: "application/json" },
                });

                if (!response.ok) {
                    throw new Error();
                }

                const data = await response.json();
                this.setUid(data.uid);
            } catch (e) {
                this.error =
                    "Sorry, we couldn't start a new estimate. Please refresh and try again.";
            }
        },

        async load() {
            try {
                const response = await fetch(
                    `/price-calculator/estimates/${this.uid}`,
                    { headers: { Accept: "application/json" } }
                );

                if (!response.ok) {
                    throw new Error();
                }

                const data = await response.json();
                this.name = data.name ?? "";
                this.schools = data.schools ?? [];
                this.pricePerPupil = data.price_per_pupil;
                this.maxPrice = data.max_price;
                this.upperBand = data.upper_band;
                this.discounts = data.discounts;
                this._nextCustomId =
                    this.schools.reduce((max, s) => {
                        const match = /^custom-(\d+)$/.exec(s.id);
                        return match ? Math.max(max, Number(match[1])) : max;
                    }, 0) + 1;
            } catch (e) {
                this.error =
                    "Sorry, we couldn't load that estimate. It may have been removed, or the link may be incorrect.";
            }
        },

        setUid(uid) {
            const params = new URLSearchParams(window.location.search);
            params.set("uid", uid);
            window.history.replaceState(
                {},
                "",
                `${window.location.pathname}?${params.toString()}`
            );
            this.uid = uid;
        },

        scheduleSave() {
            if (!this.uid || this.loading) {
                return;
            }

            clearTimeout(this._saveTimeout);
            this._saveTimeout = setTimeout(() => this.save(), 600);
        },

        async save() {
            this.saving = true;

            try {
                await fetch(`/price-calculator/estimates/${this.uid}`, {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                        Accept: "application/json",
                    },
                    body: JSON.stringify({
                        name: this.name,
                        schools: this.schools,
                        price_per_pupil: this.pricePerPupil,
                        max_price: this.maxPrice,
                        upper_band: this.upperBand,
                        discounts: this.discounts,
                    }),
                });
            } catch (e) {
                // Non-fatal - the estimate still works locally even if a save fails.
            }

            this.saving = false;
        },

        async search() {
            if (!this.searchQuery.trim()) {
                return;
            }

            this.searching = true;
            this.searchError = "";
            this.searchResults = [];

            try {
                const response = await fetch(
                    `/price-calculator/trusts?search=${encodeURIComponent(
                        this.searchQuery
                    )}`,
                    { headers: { Accept: "application/json" } }
                );

                const data = await response.json();

                if (!response.ok) {
                    this.searchError =
                        data.message ?? "Sorry, something went wrong with that search.";
                } else if (data.length === 1) {
                    this.selectResult(data[0]);
                } else {
                    this.searchResults = data;
                }
            } catch (e) {
                this.searchError = "Sorry, something went wrong with that search.";
            }

            this.searching = false;
        },

        selectResult(result) {
            const schools = [];
            const excluded = [];

            (result.schools ?? []).forEach((school) => {
                const supported =
                    school.status === "Open" &&
                    SUPPORTED_PHASES.includes(school.phase);

                if (supported) {
                    schools.push({
                        id: `school-${school.id}`,
                        name: school.name,
                        pupils: parseInt(school.pupils) || 0,
                        custom: false,
                    });
                } else {
                    excluded.push(school);
                }
            });

            this.name = result.name;
            this.schools = schools;
            this.excludedSchools = excluded;
            this.searchResults = [];
            this.searchQuery = "";
            this.searchError = "";
        },

        buildCustom() {
            this.name = "";
            this.schools = [];
            this.excludedSchools = [];
            this.searchResults = [];
            this.searchError = "";
            this.addSchool();
        },

        addSchool() {
            this.schools.push({
                id: `custom-${this._nextCustomId++}`,
                name: "",
                pupils: 0,
                custom: true,
            });
        },

        removeSchool(id) {
            this.schools = this.schools.filter((s) => s.id !== id);
        },

        get started() {
            return this.schools.length > 0 || this.excludedSchools.length > 0;
        },

        get totalPupils() {
            return this.schools.reduce(
                (sum, s) => sum + (Number(s.pupils) || 0),
                0
            );
        },

        calculatePrice(pupils, discountPercent = 0) {
            pupils = Number(pupils) || 0;
            let price =
                pupils <= this.upperBand
                    ? pupils * this.pricePerPupil
                    : this.maxPrice;

            if (discountPercent) {
                price *= (100 - discountPercent) / 100;
            }

            return price;
        },

        totalForDiscount(discountPercent) {
            return this.schools.reduce(
                (sum, s) => sum + this.calculatePrice(s.pupils, discountPercent),
                0
            );
        },

        savingForDiscount(discountPercent) {
            return this.totalForDiscount(0) - this.totalForDiscount(discountPercent);
        },

        formatPrice(price) {
            return (
                "£" +
                (price ?? 0).toLocaleString("en-GB", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                })
            );
        },

        copyLink() {
            navigator.clipboard.writeText(window.location.href);
            this.copied = true;
            setTimeout(() => (this.copied = false), 4000);
        },

        print() {
            window.print();
        },
    };
}

